import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { IkoMix, IKO_KEYS } from "@/design-system";
import { confirm } from "@/lib/dialog";
import { useDeleteWindow } from "@/lib/deleteWindow";

/**
 * Admin Desk · Organisers (Vania Msg 214).
 * Full editor: name, handle, logo, bio, instagram, tiktok, website, whatsapp,
 * email, areas, iko-chip override, status, notes. Audit log per edit. Merge.
 */
const AREA_CHOICES = ["Westlands", "Kilimani", "Kileleshwa", "CBD", "Karen",
                       "Langata", "Lavington", "Kasarani", "Thika Road",
                       "Eastlands", "Gigiri", "Runda", "Hurlingham"];

export default function AdminOrganisers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [merging, setMerging] = useState(null);
  const [handingOver, setHandingOver] = useState(null);
  const [handoverEmail, setHandoverEmail] = useState("");
  const [handoverAsKibanda, setHandoverAsKibanda] = useState(false);
  const [editingOriginalHandle, setEditingOriginalHandle] = useState("");
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  // Msg 250 · delete flow state.
  const [deleteTarget, setDeleteTarget] = useState(null); // {handle,name}
  const deleteOpen = useDeleteWindow();
  const [deleteCounts, setDeleteCounts] = useState(null); // {published,pending,total}
  const [deleteMode, setDeleteMode] = useState("orphan"); // "orphan" | "merge"
  const [deleteMergeInto, setDeleteMergeInto] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/public/organisers?with_counts=true");
      const all = r.data.organisers || [];
      const ql = q.trim().toLowerCase();
      setRows(ql ? all.filter((o) => (o.handle || "").includes(ql) || (o.name || "").toLowerCase().includes(ql)) : all);
    } finally { setLoading(false); }
  };
  // WW-196 · one fetch on load: the [q] effect runs on mount too, so no separate mount fetch.
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t);
                    /* eslint-disable-next-line */ }, [q]);

  // Msg 249 · deep-link: /admin/organisers?edit=<handle> auto-opens the editor
  // so the organiser name on a queue card can link straight into the modal.
  useEffect(() => {
    const target = (params.get("edit") || "").trim().toLowerCase().replace(/^@+/, "");
    if (!target || loading) return;
    const match = rows.find((o) => (o.handle || "").toLowerCase() === target
                                 || ((o.aliases || []).map((a) => a.toLowerCase()).includes(target)));
    if (match) {
      setEditing({ ...match });
      setEditingOriginalHandle(match.handle);
      // clear the param so a manual close doesn't re-open on next state update.
      const next = new URLSearchParams(params);
      next.delete("edit");
      setParams(next, { replace: true });
    } else if (rows.length > 0) {
      // Handle not on the list yet (fresh crawl). Seed a blank row so staff
      // can fill it in and save. Upsert will create it on save.
      setEditing({ handle: target, name: target, aliases: [], areas: [], iko_chip_override: [], status: "lead" });
      setEditingOriginalHandle(target);
      const next = new URLSearchParams(params);
      next.delete("edit");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, loading]);

  const saveEdit = async () => {
    try {
      // Msg 246 · if the handle was changed, rename first so the old handle
      // is preserved as an alias (shared links keep working) and any collision
      // is reported before we touch the other fields.
      const targetHandle = (editing.handle || "").trim().toLowerCase().replace(/^@+/, "").replace(/\s+/g, "");
      if (editingOriginalHandle && targetHandle && targetHandle !== editingOriginalHandle) {
        try {
          await client.post("/admin/organisers/rename-handle",
                            { old_handle: editingOriginalHandle, new_handle: targetHandle });
        } catch (e) {
          const d = e?.response?.data?.detail;
          if (typeof d === "object" && d?.error === "handle_taken") {
            toast.error(`@${targetHandle} is already taken`);
            return;
          }
          toast.error(typeof d === "string" ? d : "handle rename failed");
          return;
        }
      }
      const body = { ...editing, handle: targetHandle || editingOriginalHandle };
      const r = await client.post("/admin/organisers/upsert", body);
      toast.success("saved");
      setEditing(null);
      setEditingOriginalHandle("");
      await load();
      return r;
    } catch (e) { toast.error(e?.response?.data?.detail || "save failed"); }
  };
  const mergeInto = async (into) => {
    if (!merging || !into) return;
    if (!(await confirm(`Move all events from @${merging.handle} into @${into.handle}? This cannot be undone.`))) return;
    try {
      await client.post("/admin/organisers/merge", { from_handle: merging.handle, into_handle: into.handle });
      toast.success("merged");
      setMerging(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "merge failed"); }
  };
  const handover = async () => {
    if (!handingOver || !handoverEmail || !handoverEmail.includes("@")) {
      toast.error("valid email required"); return;
    }
    const path = handoverAsKibanda
      ? "/admin/organisers/handover-as-kibanda"
      : "/admin/organisers/handover";
    try {
      const r = await client.post(path,
                        { handle: handingOver.handle, manager_email: handoverEmail });
      if (handoverAsKibanda) {
        const seat = r?.data?.founding_seat_number || 0;
        const rem = r?.data?.founding_seats_remaining;
        toast.success(seat
          ? `kibanda started · founding seat #${seat} (${rem} left)`
          : `kibanda started · standard tier`);
      } else {
        toast.success(`handed to ${handoverEmail}`);
      }
      setHandingOver(null); setHandoverEmail(""); setHandoverAsKibanda(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "handover failed"); }
  };

  // Msg 250 · staff delete with confirm + event-count guard + merge/orphan choice.
  const openDelete = async (o) => {
    setDeleteTarget(o); setDeleteCounts(null); setDeleteMode("merge"); setDeleteMergeInto("");
    try {
      const r = await client.get(`/admin/organisers/${encodeURIComponent(o.handle)}/event-counts`);
      setDeleteCounts(r.data);
    } catch { setDeleteCounts({ published: 0, pending: 0, total: 0 }); }
  };
  const confirmDelete = async () => {
    if (!deleteTarget || !deleteCounts) return;
    const mode = deleteCounts.total === 0 ? "auto" : deleteMode;
    if (mode === "merge") {
      const t = (deleteMergeInto || "").trim().toLowerCase().replace(/^@+/, "");
      if (!t || t === deleteTarget.handle) { toast.error("pick a different curator to merge into"); return; }
    }
    try {
      await client.post("/admin/organisers/delete-safely", {
        handle: deleteTarget.handle,
        mode,
        into_handle: mode === "merge" ? deleteMergeInto.replace(/^@+/, "") : null,
      });
      toast.success(mode === "merge" ? `merged into @${deleteMergeInto}` : "deleted");
      setDeleteTarget(null); setDeleteCounts(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || "delete failed");
    }
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const handles = rows.filter((r) => selectedIds.has(r.handle));
    const msg = `delete ${handles.length} curator${handles.length === 1 ? "" : "s"}? curators that still have events are skipped (move their events first with delete… on the row). this can't be undone.`;
    if (!(await confirm(msg))) return;
    try {
      await client.post("/admin/organisers/bulk-delete", {
        items: handles.map((h) => ({ handle: h.handle, mode: "auto" })),
      });
      toast.success(`processed ${handles.length}`);
      setSelectedIds(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "bulk delete failed"); }
  };
  const toggleSelect = (h) => {
    setSelectedIds((s) => {
      const n = new Set(s); if (n.has(h)) n.delete(h); else n.add(h); return n;
    });
  };

  return (
    <div className="p-6" data-testid="admin-organisers">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">curators</h1>
        <p className="text-sm text-neutral-500">{rows.length} in the list</p>
      </div>
      <input placeholder="filter by name or @handle" value={q} onChange={(e) => setQ(e.target.value)}
             data-testid="admin-organisers-search"
             className="mt-3 w-full max-w-md rounded-md border border-neutral-300 p-2 text-sm" />

      {selectedIds.size > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-neutral-900 bg-neutral-900 px-3 py-2 text-xs text-white"
             data-testid="admin-organisers-bulk-bar">
          <span className="font-semibold">{selectedIds.size} selected</span>
          <button onClick={() => setSelectedIds(new Set())} className="rounded px-2 py-1 hover:bg-white/10">clear</button>
          {deleteOpen && <button onClick={bulkDelete} data-testid="admin-organisers-bulk-delete"
                  className="ml-auto rounded bg-red-500 px-2 py-1 font-semibold hover:bg-red-600">delete selected</button>}
        </div>
      )}

      {loading ? (
        <p className="mt-8 text-sm text-neutral-500">loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-sm text-neutral-500">no curators match.</p>
      ) : (
      <div className="mt-6 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-neutral-500">
              <th className="pb-2"><input type="checkbox" aria-label="select all"
                                             checked={rows.length > 0 && selectedIds.size === rows.length}
                                             onChange={(e) => setSelectedIds(e.target.checked ? new Set(rows.map((r) => r.handle)) : new Set())} /></th>
              <th className="pb-2">Name</th><th>@Handle</th><th>Iko mix</th><th>Upcoming</th><th>Links</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.handle} className="border-t border-neutral-200 align-top"
                  data-testid={`admin-organiser-row-${o.handle}`}>
                <td className="py-2 pr-3">
                  <input type="checkbox" aria-label={`select ${o.handle}`}
                         data-testid={`admin-organiser-select-${o.handle}`}
                         checked={selectedIds.has(o.handle)} onChange={() => toggleSelect(o.handle)} />
                </td>
                <td className="py-2 pr-3 font-semibold">{(o.name || o.handle).toLowerCase()}</td>
                <td className="py-2 pr-3">@{o.handle}</td>
                <td className="py-2 pr-3" style={{ minWidth: 180 }}>
                  <IkoMix counts={o.iko_mix?.counts || {}} total={o.iko_mix?.total || 0}
                          overrideChips={o.iko_chip_override} label="usually runs" compact
                          testId={`admin-iko-mix-${o.handle}`} />
                </td>
                <td className="py-2 pr-3">{o.upcoming_events_count || 0}</td>
                <td className="py-2 pr-3 text-[11px] text-neutral-500">
                  {o.instagram_url ? "ig " : ""}{o.tiktok_url ? "tt " : ""}{o.website ? "web " : ""}{o.whatsapp ? "wa" : ""}
                </td>
                <td className="py-2 text-right">
                  <button className="mr-2 rounded-md border border-neutral-300 px-2 py-1 text-xs"
                          onClick={() => { setEditing({ ...o }); setEditingOriginalHandle(o.handle); }}
                          data-testid={`edit-${o.handle}`}>edit</button>
                  <button className="mr-2 rounded-md border border-neutral-300 px-2 py-1 text-xs"
                          onClick={() => setHandingOver(o)}
                          data-testid={`handover-${o.handle}`}>hand over…</button>
                  <button className="mr-2 rounded-md border border-neutral-300 px-2 py-1 text-xs"
                          onClick={() => setMerging(o)}
                          data-testid={`merge-${o.handle}`}>merge into…</button>
                  {deleteOpen && <button className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          onClick={() => openDelete(o)}
                          data-testid={`delete-${o.handle}`}>delete…</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {editing && (
        <Modal onClose={() => { setEditing(null); setEditingOriginalHandle(""); }}>
          <p className="mb-3 text-base font-semibold">edit curator</p>
          {editingOriginalHandle && editing.handle && editing.handle !== editingOriginalHandle && (
            <p className="mb-2 rounded-md bg-amber-50 p-2 text-[11px] text-amber-800">
              you're renaming <strong>@{editingOriginalHandle}</strong> → <strong>@{editing.handle}</strong>. old handle stays as an alias so links still work.
            </p>
          )}
          <Field label="display name" v={editing.name} on={(v) => setEditing({ ...editing, name: v })} testId="edit-name" />
          <Field label="@handle" v={editing.handle} on={(v) => setEditing({ ...editing, handle: v.toLowerCase().replace(/^@+/, "").replace(/\s+/g, "") })} testId="edit-handle" />
          <Field label="logo url" v={editing.logo_url} on={(v) => setEditing({ ...editing, logo_url: v })} testId="edit-logo" />
          <FieldTA label="bio" v={editing.bio} on={(v) => setEditing({ ...editing, bio: v })} testId="edit-bio" />
          <Field label="website" v={editing.website} on={(v) => setEditing({ ...editing, website: v })} testId="edit-website" />
          <Field label="instagram (@ or url)" v={editing.instagram_url} on={(v) => setEditing({ ...editing, instagram_url: v })} testId="edit-instagram" />
          <Field label="tiktok (@ or url)" v={editing.tiktok_url} on={(v) => setEditing({ ...editing, tiktok_url: v })} testId="edit-tiktok" />
          <Field label="x / twitter (@ or url)" v={editing.x_url} on={(v) => setEditing({ ...editing, x_url: v })} testId="edit-x" />
          <Field label="facebook url" v={editing.facebook_url} on={(v) => setEditing({ ...editing, facebook_url: v })} testId="edit-facebook" />
          <Field label="youtube url" v={editing.youtube_url} on={(v) => setEditing({ ...editing, youtube_url: v })} testId="edit-youtube" />
          <Field label="linkedin url" v={editing.linkedin_url} on={(v) => setEditing({ ...editing, linkedin_url: v })} testId="edit-linkedin" />
          <Field label="whatsapp (number or wa.me/…)" v={editing.whatsapp} on={(v) => setEditing({ ...editing, whatsapp: v })} testId="edit-whatsapp" />
          <Field label="contact email (staff only, never public)" v={editing.contact_email} on={(v) => setEditing({ ...editing, contact_email: v })} testId="edit-email" />
          <Field label="contact phone (staff only, never public)" v={editing.contact_phone} on={(v) => setEditing({ ...editing, contact_phone: v })} testId="edit-phone" />

          <label className="mt-3 block text-[11px] uppercase tracking-[0.14em] text-neutral-500">areas</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {AREA_CHOICES.map((a) => {
              const on = (editing.areas || []).includes(a);
              return (
                <button key={a} type="button"
                        onClick={() => setEditing({
                          ...editing,
                          areas: on ? editing.areas.filter((x) => x !== a) : [...(editing.areas || []), a],
                        })}
                        className="rounded-full border px-3 py-1 text-xs"
                        style={{ background: on ? "#1A1A30" : "transparent",
                                 color: on ? "#FFD60A" : "#1A1A30",
                                 borderColor: on ? "#1A1A30" : "#CCC" }}>
                  {a}
                </button>
              );
            })}
          </div>

          <label className="mt-4 block text-[11px] uppercase tracking-[0.14em] text-neutral-500">iko chip override (leave empty to auto)</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {IKO_KEYS.map((k) => {
              const on = (editing.iko_chip_override || []).includes(k);
              return (
                <button key={k} type="button"
                        onClick={() => setEditing({
                          ...editing,
                          iko_chip_override: on ? editing.iko_chip_override.filter((x) => x !== k) : [...(editing.iko_chip_override || []), k].slice(0, 2),
                        })}
                        className="rounded-full border px-3 py-1 text-xs"
                        style={{ background: on ? "#1A1A30" : "transparent",
                                 color: on ? "#FFD60A" : "#1A1A30",
                                 borderColor: on ? "#1A1A30" : "#CCC" }}>
                  {k}
                </button>
              );
            })}
          </div>

          <label className="mt-4 block text-[11px] uppercase tracking-[0.14em] text-neutral-500">status</label>
          <select value={editing.status || "lead"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                  data-testid="edit-status"
                  className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm">
            {["lead", "contacted", "claimed", "partner"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <FieldTA label="notes" v={editing.notes} on={(v) => setEditing({ ...editing, notes: v })} testId="edit-notes" />

          {(editing.edits || []).length > 0 && (
            <div className="mt-4 rounded-md bg-neutral-50 p-3 text-[11px]" data-testid="edit-audit">
              <p className="mb-1 font-semibold uppercase tracking-[0.14em] text-neutral-500">audit log</p>
              <ul className="max-h-32 overflow-y-auto">
                {(editing.edits || []).slice(-8).reverse().map((e, i) => (
                  <li key={i} className="border-t border-neutral-200 py-1">
                    <span className="text-neutral-500">{(e.at || "").slice(0, 19).replace("T", " ")}</span>
                    {" · "}<strong>{e.field}</strong>
                    {" · "}<span className="text-neutral-500">by {(e.by_user_id || "?").slice(0, 8)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button className="rounded-md border border-neutral-300 px-3 py-2 text-sm" onClick={() => { setEditing(null); setEditingOriginalHandle(""); }}>cancel</button>
            <button onClick={saveEdit} data-testid="edit-save"
                    className="rounded-md px-4 py-2 text-sm"
                    style={{ background: "#FFD60A", color: "#1A1A30", fontWeight: 700 }}>save</button>
          </div>
        </Modal>
      )}

      {handingOver && (
        <Modal onClose={() => { setHandingOver(null); setHandoverEmail(""); setHandoverAsKibanda(false); }}>
          <p className="mb-3 text-base font-semibold">hand over @{handingOver.handle}</p>
          <p className="mb-3 text-sm text-neutral-600">
            enter the curator's email address. they'll gain manager rights on this page.
            we'll flip status to <strong>claimed</strong> and log the hand-over in the audit trail.
          </p>
          <label className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">manager email</label>
          <input value={handoverEmail} onChange={(e) => setHandoverEmail(e.target.value)}
                 data-testid="handover-email"
                 placeholder="name@organiser.co.ke" type="email"
                 className="mt-1 mb-3 w-full rounded-md border border-neutral-300 p-2 text-sm" />
          <label className="mt-2 mb-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm">
            <input type="checkbox" data-testid="handover-as-kibanda"
                   checked={handoverAsKibanda}
                   onChange={(e) => setHandoverAsKibanda(e.target.checked)} />
            <span>
              <strong>hand over as kibanda</strong> — starts a 30-day free kibanda trial and grabs a founding seat if any of the first 100 are left. no charge yet; wave 4b tracks the state only.
            </span>
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
                    onClick={() => { setHandingOver(null); setHandoverEmail(""); setHandoverAsKibanda(false); }}>cancel</button>
            <button onClick={handover} data-testid="handover-save"
                    className="rounded-md px-4 py-2 text-sm"
                    style={{ background: "#FFD60A", color: "#1A1A30", fontWeight: 700 }}>{handoverAsKibanda ? "start kibanda" : "hand over"}</button>
          </div>
        </Modal>
      )}

      {merging && (
        <Modal onClose={() => setMerging(null)}>
          <p className="mb-3 text-sm font-semibold">
            move all events from @{merging.handle} into…
          </p>
          <ul className="max-h-80 overflow-y-auto">
            {rows.filter((r) => r.handle !== merging.handle).map((r) => (
              <li key={r.handle}>
                <button onClick={() => mergeInto(r)} data-testid={`merge-into-${r.handle}`}
                        className="w-full rounded-md px-3 py-2 text-left text-sm hover:bg-neutral-100">
                  <strong>{(r.name || r.handle).toLowerCase()}</strong>
                  <span className="ml-2 text-neutral-500">@{r.handle}</span>
                </button>
              </li>
            ))}
          </ul>
        </Modal>
      )}

      {deleteTarget && (
        <Modal onClose={() => { setDeleteTarget(null); setDeleteCounts(null); }}>
          <p className="mb-2 text-base font-semibold">
            delete @{deleteTarget.handle}?
          </p>
          <p className="mb-3 text-sm text-neutral-700">
            delete <strong>{(deleteTarget.name || deleteTarget.handle).toLowerCase()}</strong>? this can't be undone.
          </p>
          {!deleteCounts ? (
            <p className="text-sm text-neutral-500">counting events…</p>
          ) : deleteCounts.total === 0 ? (
            <p className="rounded-md bg-neutral-50 p-3 text-sm text-neutral-700"
               data-testid="delete-no-events">
              no events linked. this curator will be removed.
            </p>
          ) : (
            <>
              <p className="mb-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800"
                 data-testid="delete-event-counts">
                {deleteCounts.published} published, {deleteCounts.pending} pending still point at this curator. pick the curator they move to before deleting:
              </p>
              <label className="mb-2 flex items-start gap-2 text-sm">
                <input type="radio" name="del-mode" data-testid="delete-mode-merge"
                       checked={deleteMode === "merge"}
                       onChange={() => setDeleteMode("merge")} />
                <span>
                  <strong>merge into another curator</strong> — events, follows and old handle move over.
                </span>
              </label>
              {deleteMode === "merge" && (
                <input type="text" placeholder="type the target @handle"
                       value={deleteMergeInto}
                       onChange={(e) => setDeleteMergeInto(e.target.value.trim().replace(/^@+/, "").toLowerCase())}
                       data-testid="delete-merge-into"
                       list="organiser-handles-list"
                       className="mb-3 ml-6 w-full rounded-md border border-neutral-300 p-2 text-sm" />
              )}
              <datalist id="organiser-handles-list">
                {rows.filter((r) => r.handle !== deleteTarget.handle).map((r) => (
                  <option key={r.handle} value={r.handle}>{r.name || r.handle}</option>
                ))}
              </datalist>
            </>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button className="rounded-md border border-neutral-300 px-3 py-2 text-sm"
                    onClick={() => { setDeleteTarget(null); setDeleteCounts(null); }}>cancel</button>
            <button onClick={confirmDelete} data-testid="delete-confirm"
                    disabled={!deleteCounts}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {deleteCounts?.total === 0 ? "delete" : "move events and delete"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 60, display: "grid", placeItems: "center" }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#FFFFFF", borderRadius: 12, padding: 20, width: "92%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, v, on, testId }) {
  return (
    <>
      <label className="mt-2 block text-[11px] uppercase tracking-[0.14em] text-neutral-500">{label}</label>
      <input value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId}
             className="mb-1 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />
    </>
  );
}
function FieldTA({ label, v, on, testId }) {
  return (
    <>
      <label className="mt-2 block text-[11px] uppercase tracking-[0.14em] text-neutral-500">{label}</label>
      <textarea value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId}
                rows={3}
                className="mb-1 mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm" />
    </>
  );
}
